/**
 * LandingCompetitorReality — fragmented market vs AGEON opportunity pillars.
 */
import {
    ArrowRight,
    Building2,
    ClipboardList,
    FlaskConical,
    Gauge,
    Layers,
    ScanFace,
    Sparkles,
    Stethoscope,
} from 'lucide-react';

const FRAGMENTED = [
    {
        title: 'Longevity clinics',
        desc: 'High-end offerings with bespoke approaches — but little systemization.',
        icon: Building2,
    },
    {
        title: 'Aesthetic & recovery centres',
        desc: 'Appearance and short-term outcomes without longitudinal tracking.',
        icon: ScanFace,
    },
    {
        title: 'Biomarker & test brands',
        desc: 'Advanced diagnostics with limited holistic protocol integration.',
        icon: FlaskConical,
    },
    {
        title: 'Traditional wellness clinics',
        desc: 'Broad services with limited protocol depth and measurable outcomes.',
        icon: Stethoscope,
    },
];

const OPPORTUNITY = [
    { title: 'Integrated protocols', desc: 'Assessment-led journeys, not isolated sessions.', icon: ClipboardList },
    { title: 'Premium experience', desc: 'Architectural, calm, member-first delivery.', icon: Sparkles },
    { title: 'Measurable outcomes', desc: 'AGEON Score and tracked progress over time.', icon: Gauge },
    { title: 'Scalable model', desc: 'Protocol-led operations built for national rollout.', icon: Layers },
];

const MARKET_PLAYERS = ['AIWO', 'Decode Age', 'BioPeak', 'Premium preventive'];

const LABEL_CLASS = 'mb-6 text-[11px] font-bold uppercase tracking-[0.18em] text-ink-500';

function LandscapeCard({ title, desc, icon: Icon, delay }) {
    return (
        <article
            className="group flex h-full gap-4 rounded-2xl bg-white p-5 ring-1 ring-ink-100 shadow-[0_8px_28px_-14px_rgba(15,17,21,0.08)] transition hover:ring-ink-200 sm:p-6"
            data-aos="fade-up"
            data-aos-delay={String(delay)}
        >
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-ink-50 text-ink-500 ring-1 ring-ink-100 transition group-hover:bg-brand-teal-50 group-hover:text-brand-teal-700 group-hover:ring-brand-teal/15">
                <Icon className="h-5 w-5" strokeWidth={1.75} />
            </span>
            <div className="min-w-0">
                <h3 className="font-heading text-sm font-bold text-ink-900 sm:text-base">{title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-ink-500">{desc}</p>
            </div>
        </article>
    );
}

function OpportunityPillar({ title, desc, icon: Icon, delay }) {
    return (
        <div
            className="h-full rounded-2xl bg-white/10 p-5 ring-1 ring-white/15 backdrop-blur-sm"
            data-aos="fade-left"
            data-aos-delay={String(delay)}
        >
            <span className="grid h-10 w-10 place-items-center rounded-lg bg-white/15 text-white">
                <Icon className="h-[1.1rem] w-[1.1rem]" strokeWidth={1.75} />
            </span>
            <h3 className="mt-4 font-heading text-sm font-bold text-white">{title}</h3>
            <p className="mt-1.5 text-sm leading-relaxed text-white/60">{desc}</p>
        </div>
    );
}

export default function LandingCompetitorReality() {
    return (
        <section id="competitor-reality" className="relative scroll-mt-24 overflow-hidden bg-ink-50 border-t border-ink-100/80">
            <div aria-hidden className="pointer-events-none absolute inset-0 landing-grain opacity-30" />
            <div
                aria-hidden
                className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_55%_40%_at_0%_100%,rgba(0,176,155,0.06),transparent_55%)]"
            />

            <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-20 lg:py-28">
                <div className="max-w-3xl" data-aos="fade-up">
                    <span className="lp-chip mb-6">Competitor reality</span>
                    <h2 className="font-heading text-3xl font-extrabold leading-[1.12] tracking-tight text-ink-900 sm:text-4xl lg:text-[2.65rem]">
                        The category is active.{' '}
                        <span className="bg-gradient-to-r from-brand-teal to-brand-teal-700 bg-clip-text text-transparent">
                            The white space is systemization.
                        </span>
                    </h2>
                    <p className="mt-6 text-base leading-relaxed text-ink-600 sm:text-lg">
                        AIWO, Decode Age, BioPeak, and premium preventive players are educating the market —
                        but the category remains fragmented.
                    </p>
                    <div className="mt-6 flex flex-wrap gap-2">
                        {MARKET_PLAYERS.map(name => (
                            <span
                                key={name}
                                className="inline-flex rounded-full bg-white px-3.5 py-1.5 text-xs font-semibold text-ink-600 ring-1 ring-ink-100"
                            >
                                {name}
                            </span>
                        ))}
                    </div>
                </div>

                <div className="mt-12 sm:mt-14 lg:mt-16">
                    <p className={LABEL_CLASS} data-aos="fade-up" data-aos-delay="60">
                        Fragmented landscape
                    </p>

                    <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-[1fr_auto_1fr] lg:gap-6 xl:gap-8">
                        {/* Fragmented landscape cards */}
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2" data-aos="fade-right">
                            {FRAGMENTED.map((item, i) => (
                                <LandscapeCard
                                    key={item.title}
                                    title={item.title}
                                    desc={item.desc}
                                    icon={item.icon}
                                    delay={80 + i * 60}
                                />
                            ))}
                        </div>

                        {/* Connector — vertically centered between card blocks */}
                        <div
                            className="flex items-center justify-center lg:px-2"
                            aria-hidden
                            data-aos="fade-up"
                            data-aos-delay="200"
                        >
                            <span className="grid h-12 w-12 place-items-center rounded-full bg-white text-brand-teal-700 shadow-[0_8px_24px_-8px_rgba(0,176,155,0.35)] ring-1 ring-brand-teal/20 lg:h-14 lg:w-14">
                                <ArrowRight className="h-5 w-5 rotate-90 lg:rotate-0" strokeWidth={2.5} />
                            </span>
                        </div>

                        {/* Opportunity pillars */}
                        <div
                            className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-brand-teal via-brand-teal-700 to-[#065f56] p-5 shadow-[0_32px_64px_-24px_rgba(0,140,124,0.5)] sm:p-6"
                            data-aos="fade-left"
                            data-aos-delay="120"
                        >
                            <div aria-hidden className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-white/10 blur-3xl" />
                            <div aria-hidden className="pointer-events-none absolute -bottom-16 -left-8 h-44 w-44 rounded-full bg-brand-gold/15 blur-3xl" />

                            <div className="relative grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
                                {OPPORTUNITY.map((item, i) => (
                                    <OpportunityPillar
                                        key={item.title}
                                        title={item.title}
                                        desc={item.desc}
                                        icon={item.icon}
                                        delay={140 + i * 70}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
